import React, { useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import Icon from '../icon';
import { useOutClickHook } from '../../../../utils/hooks';
import { accessibleOnClick } from '../../../../utils/accessible-on-click';
import Button from '../button';
import * as Icons from '../../../../svg';
import { OverlayTooltip, Placement } from '../../overlay';
import { useCanShowDataExport } from '../../../../utils/data-export-permission';
import { useExportConfirmation } from '../../../../components/export-confirmation/use-export-confirmation';

type ExportButtonProps = {
  handleExport: () => void;
  handleConsolidatedExport?: () => void;
  isDropdownFlow?: boolean;
  iconOnly?: boolean;
  customTooltupText?: string;
  disableExportBtn?: boolean;
  isLoading?: boolean;
  // Direct-download exports (e.g. mailbox emails) can't be routed to the owner,
  // so they opt out of the mode-2 confirmation dialog and only honour mode 0/1.
  skipExportConfirmation?: boolean;
};

const ExportButton: React.FC<ExportButtonProps> = ({
  handleExport,
  handleConsolidatedExport,
  isDropdownFlow = false,
  iconOnly = false,
  customTooltupText,
  disableExportBtn = false,
  isLoading = false,
  skipExportConfirmation = false,
}) => {
  const { t } = useTranslation();
  const [showDropdown, setShowDropdown] = useState(false);
  const closeDropdown = () => setShowDropdown(false);
  const toggleDropdown = () => setShowDropdown(!showDropdown);
  const dropdownRef = useRef(null);
  useOutClickHook(dropdownRef, closeDropdown);
  const canDisplayDataExport = useCanShowDataExport();
  const { guardExport, exportConfirmationModal } = useExportConfirmation();

  if (!canDisplayDataExport) {
    return null;
  }

  const onExport = () =>
    skipExportConfirmation ? handleExport() : guardExport(handleExport);
  const onConsolidatedExport = () => {
    if (!handleConsolidatedExport) {
      return;
    }
    if (skipExportConfirmation) {
      handleConsolidatedExport();
    } else {
      guardExport(handleConsolidatedExport);
    }
  };

  if (!isDropdownFlow) {
    if (iconOnly) {
      return (
        <>
          <OverlayTooltip
            placement={Placement.Bottom}
            text={customTooltupText || 'Export'}
          >
            <Button
              className="d-flex justify-content-center action-btn--export icon-only"
              onClick={onExport}
              theme={Button.Theme.Ghost}
              iconLeft={<Icon className="font-20" identifier="download" />}
              isLoading={isLoading}
              disabled={disableExportBtn}
            />
          </OverlayTooltip>
          {exportConfirmationModal}
        </>
      );
    }

    return (
      <>
        <Button
          className="d-flex justify-content-center action-btn--export"
          onClick={onExport}
          theme={Button.Theme.Ghost}
          iconLeft={<Icon className="font-20" identifier="download" />}
          disabled={disableExportBtn}
          isLoading={isLoading}
        >
          {iconOnly ? '' : t('labels.export')}
        </Button>
        {exportConfirmationModal}
      </>
    );
  }

  return (
    <>
      <div
        ref={dropdownRef}
        className="d-flex align-items-center pointer"
        {...accessibleOnClick(toggleDropdown)}
      >
        <Icons.Download
          height={20}
          width={20}
          className={`mr-2 font-20  ${
            showDropdown ? 'blue-txt-11' : 'gray-txt-15'
          }`}
        />
        <span
          className={`export-dropdown-span ${
            showDropdown ? 'blue-txt-11' : 'gray-txt-15'
          }`}
        >
          {t('labels.export')}
        </span>
        {showDropdown && (
          <div className="export-dropdown-view">
            <div className="list-unstyled profile-dropdown-hovered-list">
              <div className="menu-item">
                <OverlayTooltip
                  text="Download a detailed report of all emails sent, including metrics for opens, clicks, and replies for each Prospect"
                  placement={Placement.LeftStart}
                >
                  <div
                    className="d-flex align-items-center export-cursor-pointer"
                    {...accessibleOnClick(onConsolidatedExport)}
                  >
                    <div className="text-left">
                      <span className="regular-2 font-medium export-list-font menu-text">
                        Detailed Report
                      </span>
                    </div>
                  </div>
                </OverlayTooltip>
              </div>
              <div className="menu-item export-feature-icon-color">
                <OverlayTooltip
                  text="Export the current table view with a breakdown of the data displayed."
                  placement={Placement.LeftStart}
                >
                  <div
                    className="d-flex align-items-center export-cursor-pointer"
                    {...accessibleOnClick(onExport)}
                  >
                    <div className="text-left">
                      <span className="regular-2 font-medium export-list-font menu-text">
                        Table Breakdown
                      </span>
                    </div>
                  </div>
                </OverlayTooltip>
              </div>
            </div>
          </div>
        )}
      </div>
      {exportConfirmationModal}
    </>
  );
};

export default ExportButton;
